import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function BlogPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setMobileMenuOpen(false);
  };

  const blogPosts = [
    {
      id: 'understanding-form-1040',
      title: 'Understanding Form 1040: Complete Guide for Tax Professionals',
      excerpt: 'Master the fundamentals of Form 1040 and its schedules. Learn how AI tax automation can streamline individual income tax returns, reduce filing errors, and improve tax efficiency software performance.',
      date: 'January 15, 2025',
      readTime: '8 min read',
      category: 'Tax Forms',
      image: 'https://public.readdy.ai/ai/img_res/7987f3c3e3882c30de35e2ed5a771374.jpg',
      slug: 'understanding-form-1040'
    },
    {
      id: 'business-tax-forms-guide',
      title: 'Business Tax Forms Guide: 1065, 1120, and 1120-S Explained',
      excerpt: 'Navigate the complexities of business tax returns with professional tax software. Discover how different entity structures require different forms and how tax automation software simplifies compliance for CPAs.',
      date: 'January 10, 2025',
      readTime: '10 min read',
      category: 'Business Tax',
      image: 'https://readdy.ai/api/search-image?query=business%20professionals%20reviewing%20corporate%20tax%20documents%20and%20financial%20statements%20in%20a%20contemporary%20conference%20room%20with%20charts%20and%20graphs%20displayed%20on%20large%20monitors%20showing%20business%20performance%20metrics&width=800&height=500&seq=blog2&orientation=landscape',
      slug: 'business-tax-forms-guide'
    },
    {
      id: 'state-tax-compliance',
      title: 'Multi-State Tax Compliance: Navigating 50 Different Tax Systems',
      excerpt: 'Simplify multi-state tax filing with AI-powered tax projections. Learn strategies for managing state-specific requirements and avoiding costly compliance mistakes with automated tax workflows.',
      date: 'January 5, 2025',
      readTime: '12 min read',
      category: 'State Tax',
      image: 'https://readdy.ai/api/search-image?query=detailed%20map%20of%20united%20states%20with%20tax%20documents%20and%20state%20forms%20spread%20across%20a%20professional%20workspace%20showing%20multi-state%20tax%20compliance%20planning%20with%20digital%20tools%20and%20organized%20filing%20systems&width=800&height=500&seq=blog3&orientation=landscape',
      slug: 'state-tax-compliance'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <button onClick={() => navigate('/')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('services')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Services</button>
              <button onClick={() => scrollToSection('ai-agents')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">AI Agents</button>
              <button onClick={() => navigate('/ai-agents')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">All AI Services</button>
              <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 transition-colors rounded-lg bg-emerald-50 whitespace-nowrap">Blog</button>
              <button onClick={() => scrollToSection('contact')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('services')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Services</button>
                <button onClick={() => scrollToSection('ai-agents')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">AI Agents</button>
                <button onClick={() => { navigate('/ai-agents'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">All AI Services</button>
                <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="px-4 py-3 text-sm font-medium text-emerald-700 hover:text-emerald-900 transition-colors rounded-lg bg-emerald-50 text-left whitespace-nowrap">Blog</button>
                <button onClick={() => scrollToSection('contact')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Contact</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
              <i className="ri-article-line text-emerald-600"></i>
              <span className="text-sm text-emerald-900 font-medium">Tax Insights & Resources</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 tracking-tight">
              <strong>Tax Filing Tips</strong> & <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 bg-clip-text text-transparent">Knowledge Hub</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert insights, <strong>tax compliance guides</strong>, and best practices for <strong>tax professionals</strong> navigating the complexities of modern <strong>tax compliance automation</strong> and <strong>automated tax filing</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article key={post.id} className="group bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-emerald-400 hover:shadow-xl transition-all duration-300">
                <a href={`/blog/${post.slug}`} className="block">
                  <div className="relative w-full h-64 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {post.readTime}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-emerald-600 font-semibold group-hover:gap-3 transition-all">
                      Read Article
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 tracking-tight">
            Ready to Transform Your <strong>Tax Practice</strong>?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Discover how <strong>AI-powered tax automation</strong> can streamline your workflow with <strong>automated tax filing</strong> and <strong>intelligent tax software</strong>
          </p>
          <button onClick={() => navigate('/')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
            Explore Our Solutions
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                <strong>Professional AI tax software</strong> for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge <strong>tax automation technology</strong>.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</a></li>
                <li><a href="/ai-agents" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</a></li>
                <li><a href="/blog" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</a></li>
                <li><a href="/partnerships" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Partnerships</a></li>
                <li><a href="/privacy-policy" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
